import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { DocumentChartBarIcon } from '@heroicons/react/24/outline';
import ReportTemplate from '../../components/reports/ReportTemplate';
import DateRangePicker from '../../components/reports/DateRangePicker';
import CSVExportButton from '../../components/reports/CSVExportButton';
import { ROUTES } from '../../consts/general';
import { AMAZON_TRANSACTION_DETAIL } from '../../consts/reports';
import useDateRangeParams from '../../hooks/useDateRangeParams';
import useUrlParam from '../../hooks/useUrlParam';

const PAGE_SIZE = 50;
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});
const today = new Date();
const DEFAULT_START_DATE = format(new Date(today.getFullYear(), today.getMonth(), 1), 'yyyy-MM-dd');
const DEFAULT_END_DATE = format(new Date(today.getFullYear(), today.getMonth() + 1, 0), 'yyyy-MM-dd');

const columns = [
  ['order_id', 'Order ID'], ['settlement_id', 'Settlement ID'], ['posted_at', 'Date/Time'],
  ['type', 'Type'], ['fee_category', 'Fee Category'], ['gl_account', 'GL Account'], ['sku', 'SKU'], ['asin', 'ASIN'], ['description', 'Description'], ['quantity', 'Quantity'],
  ['marketplace', 'Marketplace'], ['account_type', 'Account Type'], ['fulfillment', 'Fulfillment'],
  ['order_city', 'Order City'], ['order_state', 'Order State'], ['order_postal', 'Order Postal'],
  ['tax_collection_model', 'Tax Collection Model'], ['product_sales', 'Product Sales'],
  ['product_sales_tax', 'Product Sales Tax'], ['shipping_credits', 'Shipping Credits'],
  ['shipping_credits_tax', 'Shipping Credits Tax'], ['gift_wrap_credits', 'Gift Wrap Credits'],
  ['gift_wrap_credits_tax', 'Giftwrap Credits Tax'], ['regulatory_fee', 'Regulatory Fee'],
  ['tax_on_regulatory_fee', 'Tax On Regulatory Fee'], ['promotional_rebates', 'Promotional Rebates'],
  ['promotional_rebates_tax', 'Promotional Rebates Tax'], ['marketplace_withheld_tax', 'Marketplace Withheld Tax'],
  ['selling_fees', 'Selling Fees'], ['fba_fees', 'FBA Fees'], ['other_transaction_fees', 'Other Transaction Fees'],
  ['other', 'Other'], ['total', 'Total'], ['total_usd', 'Total (USD)'], ['fx_rate', 'FX Rate'], ['fx_rate_source', 'FX Rate Source'],
].map(([key, label]) => ({ key, label, type: ['quantity', 'fx_rate'].includes(key) ? 'number' : (key === 'posted_at' ? 'date' : (['product_sales', 'product_sales_tax', 'shipping_credits', 'shipping_credits_tax', 'gift_wrap_credits', 'gift_wrap_credits_tax', 'regulatory_fee', 'tax_on_regulatory_fee', 'promotional_rebates', 'promotional_rebates_tax', 'marketplace_withheld_tax', 'selling_fees', 'fba_fees', 'other_transaction_fees', 'other', 'total', 'total_usd'].includes(key) ? 'currency' : 'string')) }));

const money = (value, currency = 'USD') => {
  const number = Number(value);
  return Number.isFinite(number) ? number.toLocaleString('en-US', { style: 'currency', currency }) : '—';
};

const AmazonTransactionDetailReport = ({ marginFromSidebar }) => {
  const navigate = useNavigate();
  const { startDate, endDate, setDateRange } = useDateRangeParams(
    DEFAULT_START_DATE,
    DEFAULT_END_DATE
  );
  const [type, setType] = useUrlParam('type', '');
  const [basis, setBasis] = useUrlParam('basis', 'posted', (value) => ['posted', 'release'].includes(value));
  const [marketplace, setMarketplace] = useUrlParam('marketplace', 'us', (value) => ['all', 'us', 'ca', 'mx'].includes(value));
  const [data, setData] = useState([]);
  const [operatingTotals, setOperatingTotals] = useState({});
  const [nonOperatingTotals, setNonOperatingTotals] = useState({});
  const [typeBreakdown, setTypeBreakdown] = useState([]);
  const [currency, setCurrency] = useState('USD');
  const [currencyBreakdown, setCurrencyBreakdown] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ startDate, endDate, page, pageSize: PAGE_SIZE });
      if (type) params.set('type', type);
      params.set('basis', basis);
      params.set('marketplace', marketplace);
      const response = await fetch(`/api/v1/reports/${AMAZON_TRANSACTION_DETAIL}?${params}`, {
        headers: authHeaders(),
      });
      const json = await response.json();
      if (!response.ok) throw new Error(json.error || 'Failed to load report');
      setData(json.data || []);
      setOperatingTotals(json.operatingTotals || {});
      setNonOperatingTotals(json.nonOperatingTotals || {});
      setTypeBreakdown(json.typeBreakdown || []);
      setCurrency(json.currency || 'USD');
      setCurrencyBreakdown(json.currencyBreakdown || []);
      setTotalPages(json.totalPages || 1);
      setTotalRecords(json.totalRecords || 0);
    } catch (requestError) {
      setError(requestError.message);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, page, type, basis, marketplace]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [startDate, endDate, type, basis, marketplace]);

  const exportData = useCallback(async () => {
    const params = new URLSearchParams({ startDate, endDate });
    if (type) params.set('type', type);
    params.set('basis', basis);
    params.set('marketplace', marketplace);
    const response = await fetch(`/api/v1/reports/${AMAZON_TRANSACTION_DETAIL}/export?${params}`, {
      headers: authHeaders(),
    });
    if (!response.ok) throw new Error('Failed to export report');
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'amazon-transaction-detail.csv';
    link.click();
    URL.revokeObjectURL(url);
    return { handled: true };
  }, [startDate, endDate, type, basis, marketplace]);

  const filters = (
    <div className="contents">
      <select
        className="rounded border bg-white px-3 py-2 text-sm"
        value={type}
        onChange={(event) => setType(event.target.value)}
      >
        <option value="">All Types</option>
        {[...new Set(typeBreakdown.map((item) => item.type))].map((itemType) => <option key={itemType} value={itemType}>{itemType}</option>)}
      </select>
      <select
        className="rounded border bg-white px-3 py-2 text-sm"
        value={marketplace}
        onChange={(event) => setMarketplace(event.target.value)}
        aria-label="Marketplace scope"
      >
        <option value="all">All marketplaces</option>
        <option value="us">United States</option>
        <option value="ca">Canada</option>
        <option value="mx">Mexico</option>
      </select>
      <select
        className="rounded border bg-white px-3 py-2 text-sm"
        value={basis}
        onChange={(event) => setBasis(event.target.value)}
        aria-label="Reporting basis"
      >
        <option value="posted">Posted date — P&amp;L</option>
        <option value="release">Release date — cash</option>
      </select>
      <DateRangePicker startDate={startDate} endDate={endDate} onDateChange={({ startDate: start, endDate: end }) => {
        setDateRange({ startDate: start, endDate: end });
      }} />
    </div>
  );
  const pagination = (
    <div className="flex items-center justify-between border-t px-4 py-3 text-sm">
      <span>{totalRecords.toLocaleString()} rows</span>
      <div className="flex gap-2">
        <button type="button" disabled={page <= 1} onClick={() => setPage((current) => current - 1)} className="rounded border px-3 py-1 disabled:opacity-50">Previous</button>
        <span className="py-1">Page {page} of {totalPages}</span>
        <button type="button" disabled={page >= totalPages} onClick={() => setPage((current) => current + 1)} className="rounded border px-3 py-1 disabled:opacity-50">Next</button>
      </div>
    </div>
  );

  return (
    <ReportTemplate
      reportId={AMAZON_TRANSACTION_DETAIL}
      title="Amazon Transaction Detail"
      description="Amazon transaction detail sorted like the Amazon Detail workbook tab. Amounts retain Amazon's signed values."
      breadcrumbs={[{ label: 'Reports', icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" /> }, { label: '' }]}
      onBack={() => navigate(ROUTES.REPORTS)}
      marginFromSidebar={marginFromSidebar}
      isLoading={loading}
      error={error}
      filters={filters}
      exportButton={<CSVExportButton data={data} columns={columns} filename="amazon-transaction-detail" onExport={exportData} />}
      pagination={pagination}
      renderChildrenOnly
    >
      <div className="mb-3 overflow-x-auto rounded border bg-white">
        <div className="grid gap-3 border-b bg-gray-50 p-4 md:grid-cols-3">
          {typeBreakdown.map((item) => (
            <div key={`${item.type}-${item.currency}`} className="rounded bg-white p-3 shadow-sm">
              <div className="text-xs text-gray-500">{item.type}{marketplace === 'all' && item.currency ? ` (${item.currency})` : ''}{item.nonOperating ? ' (non-operating)' : ''}</div>
              <div className="font-semibold">{money(item.total, item.currency || currency)}</div>
              <div className="text-xs text-gray-500">{item.rows.toLocaleString()} rows</div>
            </div>
          ))}
        </div>
        <div className="sticky left-0 flex min-w-max items-center gap-4 border-b bg-gray-50 px-4 py-3 text-sm">
          <strong>Operating net Amazon revenue:</strong>
          {marketplace === 'all' ? (
            <span className="flex flex-wrap items-center gap-4">
              {currencyBreakdown.map((item) => (
                <span key={item.currency}>
                  <span className="text-lg font-semibold">{money(item.operatingTotal, item.currency)}</span>
                  <span className="text-xs text-gray-500"> {item.currency}</span>
                  <span className="ml-2 text-sm text-gray-600">Non-operating: {money(item.nonOperatingTotal, item.currency)}</span>
                </span>
              ))}
              <span className="text-xs text-gray-500">Not FX-converted — totals shown per currency</span>
            </span>
          ) : (
            <>
              <span className="text-lg font-semibold">{money(operatingTotals.total, currency)}</span>
              <span className="text-xs text-gray-500">Excludes transfers and reserve movements</span>
              <span className="text-sm text-gray-600">Non-operating: {money(nonOperatingTotals.total, currency)}</span>
            </>
          )}
        </div>
        {data.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <div className="mx-auto h-16 w-16 bg-gray-100 rounded-full flex items-center justify-center">
              <svg className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h3 className="mt-4 text-lg font-medium text-gray-900">No Data</h3>
            <p className="mt-2 text-sm text-gray-500">There are no Amazon transactions in the selected range.</p>
          </div>
        ) : (
          <div className="overflow-auto">
            <table className="min-w-max text-left text-xs">
              <thead className="sticky top-0 bg-gray-100">
                <tr>{columns.map((column) => <th key={column.key} className={`whitespace-nowrap px-3 py-2 ${column.type === 'currency' ? 'text-right' : ''}`}>{column.label}</th>)}</tr>
              </thead>
              <tbody>
                {data.map((row, index) => (
                  <tr key={`${row.order_id || 'empty'}-${row.posted_at}-${index}`} className={index % 2 ? 'bg-gray-50' : 'bg-white'}>
                    {columns.map((column) => <td key={column.key} className={`whitespace-nowrap px-3 py-2 ${column.type === 'currency' ? 'text-right' : ''}`}>{column.key === 'total_usd' && row.total_usd == null ? '—' : (column.type === 'currency' ? money(row[column.key], column.key === 'total_usd' ? 'USD' : (row.currency_code || 'USD')) : (row[column.key] ?? '—'))}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </ReportTemplate>
  );
};

export default AmazonTransactionDetailReport;
